import { type KeyboardEvent, type ReactNode, useMemo, useState } from 'react';
import type { UserSummary, WorkspaceMember } from '@katsquare/types';
import { Button, Spinner } from '../../components/ui';

/** Matches an in-progress "@query" at the end of the text. */
const ACTIVE_MENTION = /(?:^|\s)@([\p{L}\d_]*)$/u;

export interface MentionComposerProps {
  members: WorkspaceMember[];
  onSubmit: (body: string, mentionIds: string[]) => void;
  placeholder?: string;
  submitLabel?: string;
  multiline?: boolean;
  autoFocus?: boolean;
  busy?: boolean;
  onCancel?: () => void;
}

/**
 * A comment input with @-mention autocomplete. As the user types "@" a filtered
 * member list appears; picking one inserts "@Name " and remembers the id. On
 * submit we emit the body plus the ids whose "@Name" still appears in the text.
 */
export function MentionComposer({
  members,
  onSubmit,
  placeholder,
  submitLabel = 'Send',
  multiline = false,
  autoFocus = false,
  busy = false,
  onCancel,
}: MentionComposerProps) {
  const [value, setValue] = useState('');
  const [picked, setPicked] = useState<{ id: string; name: string }[]>([]);
  const [query, setQuery] = useState<string | null>(null);

  const suggestions = useMemo(() => {
    if (query == null) return [];
    const q = query.toLowerCase();
    return members.filter((m) => m.name.toLowerCase().includes(q)).slice(0, 6);
  }, [query, members]);

  const onChange = (v: string) => {
    setValue(v);
    const m = v.match(ACTIVE_MENTION);
    setQuery(m ? (m[1] ?? '') : null);
  };

  const pick = (member: WorkspaceMember) => {
    const at = value.lastIndexOf('@');
    if (at < 0) return;
    setValue(`${value.slice(0, at)}@${member.name} `);
    setQuery(null);
    setPicked((p) => (p.some((x) => x.id === member.id) ? p : [...p, { id: member.id, name: member.name }]));
  };

  const submit = () => {
    const body = value.trim();
    if (!body) return;
    const mentionIds = picked.filter((p) => body.includes(`@${p.name}`)).map((p) => p.id);
    onSubmit(body, mentionIds);
    setValue('');
    setPicked([]);
    setQuery(null);
  };

  const onKeyDown = (e: KeyboardEvent) => {
    if (suggestions.length > 0 && (e.key === 'Enter' || e.key === 'Tab')) {
      e.preventDefault();
      pick(suggestions[0]!);
      return;
    }
    if (e.key === 'Escape' && query != null) {
      e.preventDefault();
      setQuery(null);
      return;
    }
    // Single-line: Enter submits. Multiline: Cmd/Ctrl+Enter submits.
    if (e.key === 'Enter' && (!multiline || e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      submit();
    }
  };

  const fieldClass =
    'w-full rounded-md border border-neutral-300 bg-white px-3 py-2 text-sm text-neutral-900 outline-none placeholder:text-neutral-400 focus:border-neutral-900 focus:ring-1 focus:ring-neutral-900';

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="relative">
        {multiline ? (
          <textarea
            autoFocus={autoFocus}
            value={value}
            rows={3}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={placeholder}
            className={`${fieldClass} resize-none`}
          />
        ) : (
          <input
            autoFocus={autoFocus}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={placeholder}
            className={fieldClass}
          />
        )}

        {suggestions.length > 0 && (
          <ul className="absolute bottom-full left-0 z-10 mb-1 max-h-48 w-full overflow-y-auto rounded-md border border-neutral-200 bg-white py-1 shadow-lg">
            {suggestions.map((m, i) => (
              <li key={m.id}>
                <button
                  type="button"
                  onClick={() => pick(m)}
                  className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm ${
                    i === 0 ? 'bg-neutral-100' : 'hover:bg-neutral-50'
                  }`}
                >
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-blue-600 text-[10px] font-semibold text-white">
                    {m.name.charAt(0).toUpperCase()}
                  </span>
                  <span className="truncate text-neutral-900">{m.name}</span>
                  <span className="ml-auto truncate text-xs text-neutral-400">{m.email}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="mt-2 flex items-center justify-end gap-2">
        {multiline && <span className="mr-auto text-[10px] text-neutral-400">⌘/Ctrl+Enter to send</span>}
        {onCancel && (
          <Button type="button" variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        )}
        <Button type="submit" disabled={busy || !value.trim()} className={multiline ? '' : 'px-2 py-1 text-xs'}>
          {busy ? <Spinner /> : submitLabel}
        </Button>
      </div>
    </form>
  );
}

/** Render a comment body with @mentions highlighted. */
export function renderWithMentions(body: string, mentions: UserSummary[]): ReactNode {
  if (mentions.length === 0) return body;
  // Longest names first so "@Ann Lee" wins over "@Ann".
  const names = [...new Set(mentions.map((m) => m.name))].sort((a, b) => b.length - a.length);
  const escaped = names.map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const re = new RegExp(`@(?:${escaped.join('|')})`, 'g');

  const out: ReactNode[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  let key = 0;
  while ((m = re.exec(body)) !== null) {
    if (m.index > last) out.push(body.slice(last, m.index));
    out.push(
      <span key={key++} className="rounded bg-blue-100 px-0.5 font-medium text-blue-700">
        {m[0]}
      </span>,
    );
    last = m.index + m[0].length;
  }
  if (last < body.length) out.push(body.slice(last));
  return out;
}
